import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "src/store";

import { selectGroupById, selectGroups } from "@dashboard/groups/store/selectors";
import { PaneElement } from "@dashboard/shared/components/SidePanel/shared/PaneElement";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { showDetails, updateGroup } from "@devices/store";
import { License } from "@devices/types";
import { getNestedGroupsWithIndentation } from "@groups/utils";
import { PermissionService } from "@services/permissionService/permissionService";
import { TextLink } from "@shared/TextLink";
import { showSuccessToast } from "@shared/toasts/Toasts";
import { PropertyKey, PropertyRow, PropertyValue } from "@sidePanel/shared/Property";

type Props = Pick<License, "id" | "groupId" | "groupName">;

/** The group a device slot belongs to, and the control that moves it into another one. */
export const GroupPropertyRow = (props: Props) => {
    const dispatch = useDispatch();
    const { t } = useTranslation(["common", "sidepanels"]);

    const group = useSelector((state) => selectGroupById(state, props.groupId));
    const groups = useSelector(selectGroups);
    const { data: workspace } = useCurrentWorkspace();
    const { groupAbility } = PermissionService();

    const canMove = groupAbility(group ?? null, workspace).can("update", "Group");
    const options = getNestedGroupsWithIndentation(groups).filter((option) =>
        groupAbility(groups.find((item) => item.id === option.value) ?? null, workspace).can("update", "Group"),
    );

    const getDisabledTooltip = () => {
        if (!canMove) {
            return t("sidepanels:devicePanel.tooltip.noPermissionToMoveDevice");
        }
        if (!options.length) {
            return t("sidepanels:devicePanel.tooltip.noGroupsAvailable");
        }
        return undefined;
    };

    const onMove = async (value: string | number | null) => {
        if (typeof value !== "number" || value === props.groupId) {
            return;
        }

        await dispatch(updateGroup({ id: value, addedLicenseIds: [props.id], includeStatistics: true, includeParents: true }))
            .unwrap()
            .then(() => {
                showSuccessToast(t("sidepanels:moveLicensePanel.toast.movedLicenseSuccess", { count: 1 }));
            })
            .catch((error: { message?: string }) => {
                throw new Error(error.message || t("sidepanels:moveLicensePanel.toast.movedLicenseFailed", { count: 1 }));
            });
    };

    return (
        <PropertyRow>
            <PropertyKey>{t("sidepanels:devicePanel.overviewTab.group")}</PropertyKey>
            <PropertyValue>
                <PaneElement.EditableSelectInput
                    initialValue={props.groupId}
                    label={
                        <TextLink
                            onClick={() => dispatch(showDetails({ type: "group", selectedId: props.groupId }))}
                            tooltip={t("common:tooltip.showGroupDetails")}
                            truncatedTooltip={props.groupName ?? "-"}
                        >
                            {props.groupName ?? "-"}
                        </TextLink>
                    }
                    editAction="move"
                    options={options}
                    onSave={onMove}
                    disabledTooltip={getDisabledTooltip()}
                    disableClearable
                    disableSorting
                />
            </PropertyValue>
        </PropertyRow>
    );
};
